import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import { usePopper } from "react-popper";
import { classNames } from "../../../helpers/classNames";
import dayjs from "dayjs";
import SingleDropdown from "./SingleDropdown";
import RengeDropdown from "./RengeDropdown";

function convertToISOString({
  selectedValue,
  referenceDate = new Date(),
  timezone,
}) {
  const date = new Date(
    `${dayjs(referenceDate).format("YYYY-MM-DD")} ${selectedValue} ${timezone}`
  );
  return date.toISOString();
}

const timeZone = ["GMT", "PST", "EST"];

const TimePicker = ({
  inputClass,
  placeholder = "Select time",
  format = 12,
  second = true,
  minute = true,
  onChange = () => {},
  value,
  allowClear = true,
  timezone = true,
  range = false,
  label,
  labelClasses,
  xPlacement = "bottom",
  required,
  loading = false,
  errorType,
  errorText,
  ...props
}) => {
  const divRef = useRef(null);
  const popperElRef = useRef(null);
  const timeDropdownData = {
    hours: Array.from(
      {
        length:
          format === 24 || format === "24"
            ? format
            : format === 12 || format === "12"
            ? format
            : 24,
      },
      (_, i) => {
        if (format === 12) {
          return (i + 1).toString();
        }
        if (format === 24) {
          return i.toString().padStart(2, "0");
        }
      }
    ),
    ...(minute && {
      minutes: Array.from({ length: 60 }, (_, i) =>
        i.toString().padStart(2, "0")
      ),
    }),
    ...(second && {
      seconds: Array.from({ length: 60 }, (_, i) =>
        i.toString().padStart(2, "0")
      ),
    }),
    ...(format === 12 && { timeAt: ["AM", "PM"] }),
  };
  const [targetElement, setTargetElement] = useState(null);
  const buttonRef = useRef(targetElement);

  const [popperElement, setPopperElement] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [selectedValue, setSelectedValue] = useState(""); // ** main all selected data stored here
  const [selectedHour, setSelectedHour] = useState(timeDropdownData?.hours[0]);
  const [selectedMinute, setSelectedMinute] = useState(
    timeDropdownData?.minutes[0]
  );
  const [selectedSecond, setSelectedSecond] = useState(
    second && timeDropdownData?.seconds[0]
  );
  const [selectedTimeAt, setSelectedTimeAt] = useState(
    (format === 12 || format === "12") && timeDropdownData?.timeAt[0]
  );
  const [selectedTimezone, setSelectedTimezone] = useState(timeZone[0]);
  const [selectedRangeValue, setSelectedRangeValue] = useState({
    startTime: { hours: "1", minutes: "00", seconds: "00", timeAt: "AM" },
    endTime: { hours: "1", minutes: "00", seconds: "00", timeAt: "AM" },
    timezone: timeZone[0],
  }); // ** main all selected data stored here

  const { styles, attributes } = usePopper(targetElement, popperElement, {
    placement: xPlacement,
    modifiers: [{ name: "offset", options: { offset: [0, 5] } }],
  });

  useEffect(() => {
    if (targetElement) {
      buttonRef.current = targetElement;
    }
  }, [targetElement]);

  useMemo(() => {
    if (value) {
      if (!range) {
        // Extract hour, minute, and AM/PM
        const [time, amPm] = value.split(" "); // Splitting "12:12 AM" into ["12:12", "AM"]
        let [hour = "12", minute = "00", second = "00"] = time.split(":"); // Splitting "12:12" into ["12", "12"]

        setSelectedValue(value);
        setInputValue(value);
        setSelectedHour(hour || "00");
        if (minute) {
          setSelectedMinute(minute || "00");
        }
        if (second) {
          setSelectedSecond(second || "00");
        }
        if (format === 12 || format === "12") {
          setSelectedTimeAt(amPm || "AM");
        }
      } else {
        function parseTime(timeStr) {
          const [time, period] = timeStr?.trim().split(" ");
          const [hours, minutes] = time?.split(":");
          return {
            hours,
            minutes,
            seconds: "03", // Default seconds value
            timeAt: period,
          };
        }

        setSelectedRangeValue({
          startTime: value?.startTime
            ? parseTime(value?.startTime)
            : { hours: "1", minutes: "00", seconds: "00", timeAt: "AM" },
          endTime: value?.endTime
            ? parseTime(value?.endTime)
            : { hours: "1", minutes: "00", seconds: "00", timeAt: "AM" },
          timezone: value?.timezone ? value?.timezone.trim() : "GMT",
        });
      }
    }
  }, [value, format, second, minute, range]);

  function onChangeHandler(timeObject) {
    if (!range) {
      const {
        hours: currentHour,
        minutes: currentMinute,
        seconds: currentSecond,
        timeAt: currentTimeAt,
        timezone: currentTimezone,
      } = timeObject;

      setSelectedHour(currentHour !== undefined ? currentHour : selectedHour);

      if (minute) {
        setSelectedMinute(
          currentMinute !== undefined ? currentMinute : selectedMinute
        );
      }
      if (second) {
        setSelectedSecond(
          currentSecond !== undefined ? currentSecond : selectedSecond
        );
      }
      if (format === 12 || format === "12") {
        setSelectedTimeAt(
          currentTimeAt !== undefined ? currentTimeAt : selectedTimeAt
        );
      }

      if (timezone) {
        setSelectedSecond(
          currentTimezone !== undefined
            ? currentTimezone
            : selectedRangeValue?.timezone
        );
      }

      // Formatting the output based on minute and second flags
      let formattedTime = `${
        currentHour !== undefined ? currentHour : selectedHour
      }`;

      if (minute) {
        formattedTime += `:${
          currentMinute !== undefined ? currentMinute : selectedMinute
        }`;
      }

      if (second) {
        formattedTime += `:${
          currentSecond !== undefined ? currentSecond : selectedSecond
        }`;
      }

      if (format === 12 || format === "12") {
        formattedTime += ` ${
          currentTimeAt !== undefined ? currentTimeAt : selectedTimeAt
        }`;
      }

      if (timezone) {
        formattedTime += `:${
          currentTimezone !== undefined
            ? currentTimezone
            : selectedRangeValue?.timezone
        }`;
      }

      setInputValue(formattedTime);
      setSelectedValue(formattedTime);
      onChange(formattedTime);
    } else {
      const { startTime = {}, endTime = {}, timezone: timeZone } = timeObject;

      setSelectedRangeValue((prev) => {
        const updatedRange = {
          startTime: {
            hours: startTime.hours ?? prev.startTime.hours,
            minutes: startTime.minutes ?? prev.startTime.minutes,
            seconds: startTime.seconds ?? prev.startTime.seconds,
            timeAt: startTime.timeAt ?? prev.startTime.timeAt,
          },
          endTime: {
            hours: endTime.hours ?? prev.endTime.hours,
            minutes: endTime.minutes ?? prev.endTime.minutes,
            seconds: endTime.seconds ?? prev.endTime.seconds,
            timeAt: endTime.timeAt ?? prev.endTime.timeAt,
          },
          timezone: timeZone ?? prev?.timezone,
        };

        let formattedStartTime = `${updatedRange.startTime.hours}`;
        let formattedEndTime = `${updatedRange.endTime.hours}`;
        let formattedTimezone = `${updatedRange.timezone}`;

        if (minute) {
          formattedStartTime += `:${updatedRange.startTime.minutes}`;
          formattedEndTime += `:${updatedRange.endTime.minutes}`;
        }

        if (second) {
          formattedStartTime += `:${updatedRange.startTime.seconds}`;
          formattedEndTime += `:${updatedRange.endTime.seconds}`;
        }

        if (format === 12 || format === "12") {
          formattedStartTime += ` ${updatedRange.startTime.timeAt}`;
          formattedEndTime += ` ${updatedRange.endTime.timeAt}`;
        }

        if (timezone) {
          formattedTimezone = ` ${updatedRange.timezone}`;
        }

        const formattedRange = `${formattedStartTime} - ${formattedEndTime} ${formattedTimezone}`;

        setInputValue(formattedRange);
        onChange(
          {
            startTime: formattedStartTime,
            endTime: formattedEndTime,
            timezone: formattedTimezone,
          },
          {
            startTime: convertToISOString({
              selectedValue: formattedStartTime,
              timezone: formattedTimezone,
            }),
            endTime: convertToISOString({
              selectedValue: formattedEndTime,
              timezone: formattedTimezone,
            }),
            timezone: formattedTimezone,
          }
        );

        return updatedRange;
      });
    }
  }

  function clearHandler() {
    setSelectedValue("");
    setSelectedHour(format === "12" || format === 12 ? "01" : "00");
    if (minute) {
      setSelectedMinute("00");
    }
    if (second) {
      setSelectedSecond("00");
    }
    setInputValue("");
    onChange("");
  }

  return (
    <div className="w-full relative space-y-1">
      {label && (
        <div
          className={classNames(
            "text-xs font-medium text-shades-07 mb-1 font-CanelaDeck",
            labelClasses
          )}
        >
          {label}
          {required && (
            <span className="text-[8px] text-red-500 ml-1 absolute top-[-2px]">
              <i className="fa-solid fa-asterisk"></i>
            </span>
          )}
        </div>
      )}
      <Listbox
        as="div"
        className="w-full"
        ref={divRef}
        value={[
          ...(!range
            ? [
                {
                  hours: selectedHour,
                  ...(minute && {
                    minutes: selectedMinute,
                  }),
                  ...(second && {
                    seconds: selectedSecond,
                  }),
                  ...(format === 12 && { timeAt: selectedTimeAt }),
                },
              ]
            : [
                {
                  startTime: {
                    hours: selectedRangeValue?.startTime?.hours,
                    ...(minute && {
                      minutes: selectedRangeValue?.startTime?.minutes,
                    }),
                    ...(second && {
                      seconds: selectedRangeValue?.startTime?.seconds,
                    }),
                    ...(format === 12 && {
                      timeAt: selectedRangeValue?.startTime?.timeAt,
                    }),
                  },
                },
                {
                  endTime: {
                    hours: selectedRangeValue?.endTime?.hours,
                    ...(minute && {
                      minutes: selectedRangeValue?.endTime?.minutes,
                    }),
                    ...(second && {
                      seconds: selectedRangeValue?.endTime?.seconds,
                    }),
                    ...(format === 12 && {
                      timeAt: selectedRangeValue?.endTime?.timeAt,
                    }),
                  },
                },
              ]),
        ]}
        onChange={(e) => {
          onChangeHandler(e.at(-1));
        }}
        multiple
      >
        {({ open }) => (
          <>
            <div className="relative w-full">
              <ListboxButton
                ref={setTargetElement}
                className={classNames(
                  "w-full flex items-center h-10 border border-shades-05/50 overflow-hidden rounded-md transition-all duration-200 focus-within:border-shades-07"
                )}
              >
                {selectedValue || value?.endTime || value?.startTime ? (
                  <span className="font-CanelaDeck px-3 text-sm text-gable-green whitespace-nowrap truncate">
                    {!range
                      ? selectedValue
                      : `${value?.startTime} - ${value?.endTime} ${
                          timezone ? value?.timezone || "GMT" : ""
                        }`}
                  </span>
                ) : (
                  <span className="font-CanelaDeck px-3 text-sm text-shades-07 whitespace-nowrap truncate">
                    {placeholder}
                  </span>
                )}
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="h-full ml-auto flex-grow-0 min-w-9 w-9 flex items-center justify-center group text-shades-07"
                >
                  <i
                    className={`fa-light fa-clock block ${
                      selectedValue && allowClear && "group-hover:!hidden"
                    }`}
                  ></i>
                  <span
                    onClick={(e) => {
                      e.stopPropagation(); // prevent the Listbox from opening
                      clearHandler();
                    }}
                    className={`hidden text-opplio-grayB ${
                      selectedValue && allowClear && "group-hover:!block"
                    }`}
                  >
                    <i className={`fa-solid fa-circle-xmark`}></i>
                  </span>
                </div>
              </ListboxButton>
            </div>

            <Transition
              as={Fragment}
              className="z-50"
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
              beforeEnter={() => setPopperElement(popperElRef.current)}
              afterLeave={() => setPopperElement(null)}
            >
              <ListboxOptions
                ref={popperElRef}
                style={{
                  ...styles.popper,
                  // ...(divRef?.current?.clientWidth && {
                  //   width: divRef?.current?.clientWidth,
                  // }),
                }}
                {...attributes.popper}
                className={classNames(
                  "bg-white shadow-lg rounded-md border py-1"
                )}
              >
                <div className="max-h-96 overflow-hidden flex">
                  {!range ? (
                    <SingleDropdown
                      timeDropdownData={timeDropdownData}
                      selectedHour={selectedHour}
                      selectedMinute={selectedMinute}
                      selectedSecond={selectedSecond}
                      format={format}
                      selectedTimeAt={selectedTimeAt}
                    />
                  ) : (
                    <RengeDropdown
                      timeDropdownData={timeDropdownData}
                      selectedValue={selectedRangeValue}
                      format={format}
                    />
                  )}
                  {timezone && (
                    <div className="px-1 w-20">
                      {timeZone?.map((item, index) => (
                        <ListboxOption key={index} value={{ timezone: item }}>
                          <div
                            className={classNames(
                              "block truncate rounded px-2 py-1 hover:bg-[#0000000a] text-center text-sm text-[#000000e0] font-normal cursor-pointer",
                              range
                                ? selectedRangeValue?.timezone === item
                                  ? "!bg-chestnut-rose/70 !text-white"
                                  : ""
                                : ""
                            )}
                          >
                            {item}
                          </div>
                        </ListboxOption>
                      ))}
                    </div>
                  )}
                </div>
              </ListboxOptions>
            </Transition>
          </>
        )}
      </Listbox>
      {errorType && errorType === "danger" && (
        <div className="text-xs font-medium text-red-600">
          <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType && errorType === "warning" && (
        <div className="text-xs font-medium text-amber-500">
          <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType && errorType === "success" && (
        <div className="text-xs font-medium text-green-600">
          <i className="fa-regular fa-fw fa-circle-check text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType && errorType === "info" && (
        <div className="text-xs font-medium text-sky-600">
          <i className="fa-regular fa-fw fa-circle-info text-sm mr-1"></i>
          {errorText}
        </div>
      )}
    </div>
  );
};

export default TimePicker;
